/**
 * Logic behind the storage unit list: joining the capacity summary to its
 * roots and zones, filtering, and the small per-row derivations the table and
 * its action menu need. Pure so it is covered: this repo's vitest only
 * collects `src/**\/*.test.ts`.
 */

import type { UpdateStorageNodePayload } from '@/api/storageApi'
import type { ApiStorageNode, CapacitySummaryRow, TemperatureFamily } from '@/types/storage'

/** One capacity-summary row, joined to its root's profile/zone and flagged for the list. */
export type StorageListRow = CapacitySummaryRow & {
  storageProfileId: string | null
  zoneId: string | null
  zoneName: string | null
  /** No grid declared anywhere in the unit — not "has a nested grid elsewhere". */
  notSetUp: boolean
  needsAttention: boolean
}

/**
 * Joins a capacity summary to its roots (for the profile/zone it carries) and
 * to the zone catalog (for a name to show). `declared === 0` is what makes a
 * unit "not set up" — a unit whose capacity lives in a nested grid
 * (`layout === 'nested'`) still has one and is not flagged.
 *
 * `zones` should be the whole catalog, retired ones included: a unit still on
 * a retired zone shows its name, marked "(retired)", rather than nothing.
 */
export function buildStorageRows(
  summary: CapacitySummaryRow[],
  roots: ApiStorageNode[],
  zones: { id: string; name: string; active?: boolean }[],
): StorageListRow[] {
  const rootsById = new Map(roots.map((r) => [r.id, r]))
  const zoneNamesById = new Map(zones.map((z) => [z.id, z.active === false ? `${z.name} (retired)` : z.name]))

  return summary.map((row) => {
    const root = rootsById.get(row.nodeId)
    const zoneId = root?.zoneId ?? null
    const notSetUp = row.declared === 0
    return {
      ...row,
      storageProfileId: root?.storageProfileId ?? null,
      zoneId,
      zoneName: zoneId ? (zoneNamesById.get(zoneId) ?? null) : null,
      notSetUp,
      needsAttention: row.full || notSetUp || row.unavailable !== null,
    }
  })
}

/** What the list's search box and quick filters narrow by. */
export type StorageListFilter = {
  q: string
  family: TemperatureFamily | 'ALL'
  attention: boolean
  quick: 'full' | 'notSetUp' | null
}

/** Every filter must agree — search, family, attention and the quick toggle all AND together. */
export function filterStorageRows(rows: StorageListRow[], f: StorageListFilter): StorageListRow[] {
  const q = f.q.trim().toLowerCase()
  return rows.filter((row) => {
    if (q && !row.code.toLowerCase().includes(q) && !(row.name ?? '').toLowerCase().includes(q)) return false
    if (f.family !== 'ALL' && row.family !== f.family) return false
    if (f.attention && !row.needsAttention) return false
    if (f.quick === 'full' && !row.full) return false
    if (f.quick === 'notSetUp' && !row.notSetUp) return false
    return true
  })
}

/** The counters shown above the list. `free` only counts units that have a grid to be free in. */
export function storageListStats(rows: StorageListRow[]): {
  units: number
  free: number
  full: number
  notSetUp: number
} {
  return {
    units: rows.length,
    free: rows.filter((r) => !r.notSetUp).reduce((sum, r) => sum + r.remaining, 0),
    full: rows.filter((r) => r.full).length,
    notSetUp: rows.filter((r) => r.notSetUp).length,
  }
}

/** How many units sit at each temperature family, for the filter chips. Zero counts are omitted. */
export function familyCounts(rows: StorageListRow[]): Partial<Record<TemperatureFamily, number>> {
  const counts: Partial<Record<TemperatureFamily, number>> = {}
  for (const row of rows) {
    if (!row.family) continue
    counts[row.family] = (counts[row.family] ?? 0) + 1
  }
  return counts
}

/** The list's Space column, in the three states a unit can be in. */
export function spaceText(row: StorageListRow): string {
  if (row.declared === 0) return 'Not set up'
  if (row.full) return `Full · ${row.used} of ${row.spaces}`
  return `${row.remaining} free of ${row.spaces}`
}

/** The list's Status column. */
export function unitStatus(row: Pick<StorageListRow, 'unavailable'>): { label: 'In service' | 'Maintenance' | 'Out of service'; tone: 'ok' | 'warn' | 'bad' } {
  if (!row.unavailable) return { label: 'In service', tone: 'ok' }
  return row.unavailable.reason === 'MAINTENANCE_IN_PROGRESS'
    ? { label: 'Maintenance', tone: 'warn' }
    : { label: 'Out of service', tone: 'bad' }
}

/** What the row's ⋯ menu may offer. */
export type RowAction = 'open' | 'settings' | 'copy' | 'replace' | 'delete'

/**
 * The actions a row's menu offers, in menu order.
 *
 * `settings`, `copy` and `delete` are node edits, gated by `inventory.configure`
 * (global constraint); `copy` further needs something to copy — a row with no
 * layout at all has nothing a layout-template could describe. `replace` is
 * gated by its own `storage.replace` permission and only makes sense on a root
 * (`equipmentId` set). Without a permission the action is left off the menu
 * entirely rather than shown disabled.
 */
export function rowActions(row: StorageListRow, can: { configure: boolean; replace: boolean }): RowAction[] {
  const actions: RowAction[] = ['open']
  if (can.configure) {
    actions.push('settings')
    if (row.layout !== '') actions.push('copy')
  }
  if (can.replace && row.equipmentId) actions.push('replace')
  if (can.configure) actions.push('delete')
  return actions
}

/**
 * The payload Undo sends to put a row's Kept-at value back the way it was.
 *
 * `{ storageProfileId: null }` clears BOTH `storageProfileId` AND
 * `providesCondition` server-side (see `UpdateStorageNodePayload`), which is
 * the right undo when the row genuinely had neither — but wrong for a legacy
 * node whose condition was set directly (`providesCondition` with no
 * profile): sending it there would silently wipe a value the edit never
 * touched. That case restores `providesCondition` instead, the one field that
 * was actually set.
 */
export function keptAtUndoPatch(
  row: Pick<StorageListRow, 'storageProfileId' | 'condition'>,
): UpdateStorageNodePayload {
  if (row.storageProfileId == null && row.condition) {
    return { providesCondition: row.condition }
  }
  return { storageProfileId: row.storageProfileId }
}
